import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, call } from "@/core/api/client";
import { ENDPOINTS } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-client";
import type { components } from "@/core/api/schema";

export type Invitation = components["schemas"]["BrandInvitation"];

/** Open invitations for the signed-in email (only asked once the email is verified: the API needs that). */
export function useInvitations(enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.invitations,
    queryFn: async () => (await call(api.GET(ENDPOINTS.invitations))).data,
    enabled,
  });
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) =>
      (await call(api.POST(ENDPOINTS.invitationAccept, { params: { path: { invitationId } } }))).data,
    // A new team: the brand list and the account's memberships both change.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myBrands }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.me }),
      ]),
  });
}

export function useDeclineInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      call(api.POST(ENDPOINTS.invitationDecline, { params: { path: { invitationId } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations }),
  });
}
